import type { To, NavigateOptions } from "react-router-dom";

/**
 * Marketing deep-link URL parameters.
 *
 * Marketing links (e.g. `/?signup=true&plan=professional&interval=year` or
 * `/?signup=true&prompt=Forecast%20Q3%20revenue`) carry the user's intent into the app. An account
 * is required to reach any of these destinations, so the params are captured on the auth screen and
 * consumed at the single post-auth navigation decision point.
 *
 * They are stashed in `sessionStorage` (rather than read straight from the URL) so they survive the
 * Google OAuth round-trip, which returns to a clean URL on the same origin.
 */

/** Friendly plan name (URL) → Stripe product `feature_key` (metadata). Extend as tiers are added. */
export const PLAN_NAME_TO_FEATURE_KEY: Record<string, string> = {
  essential: "analyst-essential",
  professional: "analyst-pro",
  // max: "analyst-max",
};

type BillingInterval = "month" | "year";

const STORAGE_KEYS = {
  plan: "marketing.plan",
  interval: "marketing.interval",
  prompt: "marketing.prompt",
  returnTo: "marketing.returnTo",
} as const;

/**
 * Persist marketing params to `sessionStorage`.
 *
 * If the current URL carries *any* marketing param, the whole set is reset first, then the present
 * keys are written — so a fresh marketing link never inherits stale values from an earlier visit
 * (e.g. a `prompt` from a previous test leaking into a later `plan`-only link).
 *
 * If the URL carries *none* (e.g. the param-less Google OAuth return), the stored values are left
 * untouched so the intent captured before the redirect survives the round-trip.
 *
 * Safe to call on every mount.
 */
export const captureMarketingParams = (searchParams: URLSearchParams): void => {
  const plan = searchParams.get("plan");
  const interval = searchParams.get("interval");
  const prompt = searchParams.get("prompt");

  if (!plan && !interval && !prompt) return; // OAuth return / bare visit — preserve captured intent

  clearMarketingParams();
  if (plan) sessionStorage.setItem(STORAGE_KEYS.plan, plan);
  if (interval) sessionStorage.setItem(STORAGE_KEYS.interval, interval);
  if (prompt) sessionStorage.setItem(STORAGE_KEYS.prompt, prompt);
};

const clearMarketingParams = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.plan);
  sessionStorage.removeItem(STORAGE_KEYS.interval);
  sessionStorage.removeItem(STORAGE_KEYS.prompt);
  sessionStorage.removeItem(STORAGE_KEYS.returnTo);
};

/**
 * Remember an in-app deep link (e.g. a report link from a PDF) that a logged-out visitor opened, so login can return
 * them there. Only `/home/…` paths are kept, so this can't be used to redirect off the app.
 */
export const stashReturnTo = (pathAndSearch: string): void => {
  if (!pathAndSearch.startsWith("/home/")) return;
  sessionStorage.setItem(STORAGE_KEYS.returnTo, pathAndSearch);
};

export interface PostAuthDestination {
  to: To;
  options?: NavigateOptions;
}

/**
 * Resolve where the user should land after authenticating:
 *   - `plan` present   → the subscriptions page, which auto-launches Stripe checkout for that plan
 *   - else `prompt`    → the AI chat (prompt consumed from sessionStorage on mount)
 *   - else `returnTo`  → the deep link a logged-out visitor opened (see {@link stashReturnTo})
 *   - else             → `/home` (unchanged default)
 *
 * `plan`/`interval` are consumed here. The `prompt` is intentionally LEFT in sessionStorage so it can
 * survive the off-app Stripe round-trip and be consumed in the chat once checkout returns to the
 * success URL (or be delivered directly if checkout is skipped). See {@link consumeMarketingPrompt}.
 *
 * When both `plan` and `prompt` are present, plan wins for navigation (checkout first), but the prompt
 * now rides along to pre-fill the chat after a successful checkout.
 */
export const getPostAuthDestination = (): PostAuthDestination => {
  const plan = sessionStorage.getItem(STORAGE_KEYS.plan);
  const interval = sessionStorage.getItem(STORAGE_KEYS.interval);
  const prompt = sessionStorage.getItem(STORAGE_KEYS.prompt);
  const returnTo = sessionStorage.getItem(STORAGE_KEYS.returnTo);

  // Consume plan/interval/returnTo — the prompt is consumed later, in the chat.
  sessionStorage.removeItem(STORAGE_KEYS.plan);
  sessionStorage.removeItem(STORAGE_KEYS.interval);
  sessionStorage.removeItem(STORAGE_KEYS.returnTo);

  if (plan) {
    const normalizedInterval: BillingInterval = interval === "year" ? "year" : "month";
    const search = new URLSearchParams({ plan, interval: normalizedInterval }).toString();
    return { to: { pathname: "/home/subscriptions", search: `?${search}` } };
  }

  if (prompt) {
    return { to: { pathname: "/home/ai-analyst" } };
  }

  if (returnTo?.startsWith("/home/")) {
    return { to: returnTo };
  }

  return { to: "/home" };
};

/** Peek at the stashed marketing prompt without consuming it. */
export const peekMarketingPrompt = (): string | null =>
  sessionStorage.getItem(STORAGE_KEYS.prompt);

/** Read and remove the stashed marketing prompt — one-shot, so it pre-fills the chat exactly once. */
export const consumeMarketingPrompt = (): string | null => {
  const prompt = sessionStorage.getItem(STORAGE_KEYS.prompt);
  if (prompt) sessionStorage.removeItem(STORAGE_KEYS.prompt);
  return prompt;
};
